// Admin impersonation helpers.
//
// Flow:
//   1. Admin clicks "Open as owner" on a Never-Posted row.
//   2. FE calls POST /admin/users/:id/impersonate → backend returns
//      `{access_token, admin_token, target, expires_at}` and swaps the
//      auth cookie to the impersonation token.
//   3. FE saves `admin_token` under `tt_admin_token_backup` localStorage
//      key, saves `target` metadata under `tt_impersonation_meta`, sets
//      the primary auth token to `access_token`, and reloads → app
//      mounts as the target owner.
//   4. The `ImpersonationBanner` (mounted globally in App.js) reads
//      `tt_impersonation_meta` from localStorage and renders a sticky
//      top banner with a "Return to admin" button.
//   5. Return-to-admin posts to /admin/impersonate/stop with the
//      backup admin token, restores the cookie, clears the backup
//      keys, and reloads.
//
// Backup keys are intentionally separate from the regular auth token
// key so a normal logout doesn't accidentally lose them.

import api, { setAuthToken } from "./api";

const ADMIN_BACKUP_KEY = "tt_admin_token_backup";
const IMP_META_KEY = "tt_impersonation_meta";

export function getImpersonationMeta() {
  try {
    const raw = localStorage.getItem(IMP_META_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isImpersonating() {
  return !!getImpersonationMeta();
}

export async function startImpersonation(ownerId) {
  const { data } = await api.post(`/admin/users/${ownerId}/impersonate`);
  // Stash the admin's original token + target metadata BEFORE swapping
  // the primary auth token so a crash mid-flow can't strand the admin.
  try {
    localStorage.setItem(ADMIN_BACKUP_KEY, data.admin_token);
    localStorage.setItem(
      IMP_META_KEY,
      JSON.stringify({
        target: data.target,
        expires_at: data.expires_at,
        started_at: new Date().toISOString(),
      }),
    );
  } catch {
    /* localStorage disabled — without a backup we can't safely impersonate */
    throw new Error("Browser storage unavailable — cannot impersonate safely.");
  }
  setAuthToken(data.access_token);
  return data;
}

export async function stopImpersonation() {
  let adminToken = null;
  try {
    adminToken = localStorage.getItem(ADMIN_BACKUP_KEY);
  } catch { /* swallow */ }
  if (!adminToken) {
    // No backup — best we can do is clear local state and force re-login.
    clearImpersonationLocal();
    setAuthToken(null);
    return { restored: false };
  }
  try {
    const { data } = await api.post("/admin/impersonate/stop", { admin_token: adminToken });
    setAuthToken(data.access_token || adminToken);
    clearImpersonationLocal();
    return { restored: true, admin: data };
  } catch (e) {
    // Even if the server call fails, restore the admin token locally so
    // the admin isn't trapped — they can manually re-auth if needed.
    setAuthToken(adminToken);
    clearImpersonationLocal();
    throw e;
  }
}

export function clearImpersonationLocal() {
  try {
    localStorage.removeItem(ADMIN_BACKUP_KEY);
    localStorage.removeItem(IMP_META_KEY);
  } catch { /* swallow */ }
}
